import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, IconButton, Button } from '@mui/material';
import { Close, GetApp } from '@mui/icons-material';
import { whenCoachmarkIdle } from '../utils/coachmarkGate';

function AndroidInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // beforeinstallprompt 이벤트 리스너
    const handleBeforeInstallPrompt = (e) => {
      // 브라우저 기본 설치 프롬프트 방지
      e.preventDefault();
      
      // 이벤트 저장
      setDeferredPrompt(e);
      
      // 한 번도 안 봤거나 일정 시간이 지났으면 표시
      const hasSeenPrompt = localStorage.getItem('android-install-prompt-seen');
      const lastShown = localStorage.getItem('android-install-prompt-last-shown');
      const now = Date.now();
      
      if (!hasSeenPrompt || (lastShown && now - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000)) {
        setTimeout(() => whenCoachmarkIdle(() => setShowPrompt(true)), 3000); // 3초 후 표시 (기능 안내 코치마크가 떠 있으면 닫힌 뒤)
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 이미 설치되었는지 확인
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    // 설치 프롬프트 표시
    deferredPrompt.prompt();

    // 사용자 선택 결과 대기
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      localStorage.setItem('android-install-prompt-accepted', 'true');
    }

    // 프롬프트는 한 번만 사용 가능
    setDeferredPrompt(null);
    setShowPrompt(false);
    
    localStorage.setItem('android-install-prompt-seen', 'true');
    localStorage.setItem('android-install-prompt-last-shown', Date.now().toString());
  };

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('android-install-prompt-seen', 'true');
    localStorage.setItem('android-install-prompt-last-shown', Date.now().toString());
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <Alert
      severity="success"
      sx={{
        position: 'fixed',
        bottom: {
          xs: 'calc(70px + env(safe-area-inset-bottom, 0px))',
          sm: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        },
        left: { xs: 16, sm: 20 },
        right: { xs: 16, sm: 'auto' },
        maxWidth: { sm: 400 },
        zIndex: 9999,
        boxShadow: 6,
        borderRadius: 2,
        animation: 'slideUp 0.3s ease-out'
      }}
      action={
        <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
          <Close />
        </IconButton>
      }
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        📱 Piano Woollim 앱 설치
      </Typography>
      
      <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
        앱을 설치하면 더 빠르고 편리하게 이용할 수 있습니다.
        오프라인에서도 사용 가능합니다.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={handleClose}
          fullWidth
          sx={{ 
            textTransform: 'none',
            borderColor: 'success.main',
            color: 'success.main',
            '&:hover': {
              borderColor: 'success.dark',
              bgcolor: 'success.lighter'
            }
          }}
        >
          나중에
        </Button>
        <Button 
          size="small" 
          variant="contained" 
          onClick={handleInstall}
          startIcon={<GetApp />}
          fullWidth
          sx={{ 
            textTransform: 'none',
            bgcolor: 'success.main',
            '&:hover': {
              bgcolor: 'success.dark'
            }
          }}
        >
          설치
        </Button>
      </Box>
    </Alert>
  );
}

export default AndroidInstallPrompt;